import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getOverview } from "@apis/overview";
import { AuthContext } from "../context/AuthContext";
import { DOCS_URL } from "@utils/constant";
import { AppContext } from "../context/AppContext";
import { getPlanFromPayment, PLAN } from "@utils/payment";
import Sidebar from "@components/Sidebar";
import UpdatePlanModal from "@components/Modals/UpdatePlanModal";
import UpdatePasswordModal from "@components/Modals/UpdatePasswordModal";
import CopyButton from "@components/shared/CopyButton";
import Stats from "@components/Profile/Stats";
import { ROUTE_LOGOUT } from "@constants/routes";
import {
  firebaseAuth,
  authInstance,
  reauthenticateWithCredential,
} from "@utils/firebase";
import { useTranslation } from "react-i18next";

function Profile() {
  const { t, i18n } = useTranslation();
  const userAuth = useContext(AuthContext);
  const { user } = useContext(AppContext);
  const [overview, setOverview] = useState({
    totalRenderingCount: t("common_loading"),
    totalTemplateCount: t("common_loading"),
    monthlyRenderingCount: t("common_loading"),
  });
  const [isShowingUpdatePlanModal, setIsShowingUpdatePlanModal] =
    useState(false);
  const [isShowingUpdatePasswordModal, setIsShowingUpdatePasswordModal] =
    useState(false);
  const payment = user?.payment;
  const plan = getPlanFromPayment(payment);
  const templateLimit = plan.TEMPLATE_LIMIT;
  const monthlyRenderingLimit = plan.RENDERING_LIMIT;
  const isNotFreePlan = plan !== PLAN.FREE;
  const navigate = useNavigate();

  const logout = () => {
    navigate(ROUTE_LOGOUT);
  };

  const updatePassword = async (oldPassword: string, newPassword: string) => {
    const toastId = toast.loading("Updating password...");
    const currentUser = authInstance.currentUser;

    if (!currentUser) {
      toast.update(toastId, {
        render: "User not found",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      return;
    }

    try {
      const credential = firebaseAuth.EmailAuthProvider.credential(
        currentUser.email!,
        oldPassword
      );

      await reauthenticateWithCredential(currentUser, credential);
      await firebaseAuth.updatePassword(currentUser, newPassword);
      toast.update(toastId, {
        render: "Password updated successfully",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: (error as Error).message,
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getOverview();
        if (data) {
          const {
            totalRenderingCount,
            totalTemplateCount,
            monthlyRenderingCount,
          } = data;
          setOverview({
            totalRenderingCount: `${totalRenderingCount}`,
            totalTemplateCount: `${totalTemplateCount}`,
            monthlyRenderingCount: `${monthlyRenderingCount}`,
          });
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  return (
    <div className="flex h-full flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="profile" />
        <main className="flex-1 overflow-y-auto px-8 py-10">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
              {t("profile_title")}
            </h1>
          </div>
          <div className="space-y-8">
            {/* Stats Section */}
            <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t("profile_stats_title")}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {t("profile_stats_body")}
                </p>
              </div>
              <Stats
                overview={{
                  ...overview,
                  monthlyRenderingLimit,
                  templateLimit,
                }}
              />
            </div>

            {/* Account Information Section */}
            <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t("profile_account_info_title")}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {t("profile_account_info_body")}
                </p>
              </div>

              <div className="space-y-6">
                {/* Email Field */}
                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 sm:pt-1.5"
                  >
                    {t("profile_email_label")}
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="text"
                      name="email"
                      id="email"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm sm:leading-6"
                      disabled
                      value={userAuth?.email || ""}
                    />
                  </div>
                </div>

                {/* API Key Field */}
                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
                  <label
                    htmlFor="api-key"
                    className="block text-sm font-medium text-gray-700 sm:pt-1.5"
                  >
                    {t("profile_api_key_label")}
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <div className="flex items-center space-x-3">
                      <input
                        type="text"
                        name="api-key"
                        id="api-key"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm sm:leading-6"
                        value={user?.api_key}
                        disabled
                      />
                      <CopyButton
                        text={user?.api_key || ""}
                        onCopy={() => {
                          toast.success("Copied your API key!");
                        }}
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      {t("profile_api_key_help")}{" "}
                      <a
                        href={DOCS_URL}
                        className="font-medium text-gray-700 underline hover:text-gray-900"
                      >
                        {t("profile_view_docs")}
                      </a>
                    </p>
                  </div>
                </div>

                {/* Plan Field */}
                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
                  <label
                    htmlFor="plan"
                    className="block text-sm font-medium text-gray-700 sm:pt-1.5"
                  >
                    {t("profile_current_plan_label")}
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <div className="flex items-center space-x-3">
                      <button
                        className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                        onClick={() => {
                          setIsShowingUpdatePlanModal(true);
                        }}
                      >
                        {plan.TITLE}
                      </button>
                    </div>
                    {isNotFreePlan &&
                      payment?.status === "active" &&
                      payment?.next_bill_date && (
                        <p className="mt-2 text-sm text-gray-500">
                          {t("profile_next_billing_date")}{" "}
                          <span className="font-medium text-gray-700">
                            {payment?.next_bill_date}
                          </span>
                        </p>
                      )}
                    {isNotFreePlan &&
                      payment?.status === "deleted" &&
                      payment?.cancellation_effective_date && (
                        <p className="mt-2 text-sm text-gray-500">
                          {t("profile_plan_cancelled_until")}{" "}
                          <span className="font-medium text-gray-700">
                            {payment?.cancellation_effective_date}
                          </span>
                        </p>
                      )}
                  </div>
                </div>

                {/* Language Field */}
                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
                  <label
                    htmlFor="language"
                    className="block text-sm font-medium text-gray-700 sm:pt-1.5"
                  >
                    {t("language_label")}
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <select
                      id="language"
                      name="language"
                      className="block w-full max-w-xs rounded-lg border-0 py-2.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                      value={i18n.language}
                      onChange={(e) => {
                        i18n.changeLanguage(e.target.value);
                      }}
                    >
                      <option value="en">{t("language_en")}</option>
                      <option value="zh">{t("language_zh_tw")}</option>
                      <option value="ja">{t("language_ja")}</option>
                    </select>
                  </div>
                </div>

                {/* Auth Actions */}
                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
                  <label className="block text-sm font-medium text-gray-700 sm:pt-1.5">
                    {t("profile_account_actions")}
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <div className="flex items-center space-x-3">
                      <button
                        className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                        onClick={() => {
                          setIsShowingUpdatePasswordModal(true);
                        }}
                      >
                        {t("profile_change_password")}
                      </button>
                      <button
                        className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        onClick={logout}
                      >
                        {t("profile_sign_out")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <UpdatePasswordModal
        open={isShowingUpdatePasswordModal}
        onCancel={() => {
          setIsShowingUpdatePasswordModal(false);
        }}
        onSubmit={(oldPassword, newPassword) => {
          setIsShowingUpdatePasswordModal(false);
          updatePassword(oldPassword, newPassword);
        }}
      />
      <UpdatePlanModal
        open={isShowingUpdatePlanModal}
        onClose={() => {
          setIsShowingUpdatePlanModal(false);
        }}
      />
    </div>
  );
}

export default Profile;
